/**
 * @fileoverview Chart of Accounts model for Multi-Entity Financial Platform
 * Defines the accounting structure with 5 main categories and configurable line items
 */

/**
 * @typedef {Object} AccountCategory
 * @property {'assets'|'liabilities'|'equity'|'income'|'expenses'} type - Account category type
 * @property {string} name - Display name of the category
 * @property {string} description - Category description
 * @property {string} icon - Icon for the category
 * @property {string} color - Color theme for the category
 * @property {boolean} normalDebitBalance - True if normal balance is debit, false if credit
 */

/**
 * @typedef {Object} AccountLine
 * @property {string} id - Unique identifier for the account line
 * @property {string} name - Account line name (e.g., "Cash & Cash Equivalents")
 * @property {string} code - Account code (e.g., "1001")
 * @property {'assets'|'liabilities'|'equity'|'income'|'expenses'} category - Parent category
 * @property {string} subcategory - Subcategory (e.g., "Current Assets", "Fixed Assets")
 * @property {string} description - Detailed description
 * @property {boolean} userConfigurable - Whether users can modify this line
 * @property {string[]} dataSources - Array of data source IDs linked to this account
 * @property {number} displayOrder - Order for displaying in statements
 * @property {boolean} isActive - Whether this account line is active
 * @property {Object} metadata - Additional configuration options
 */

/**
 * @typedef {Object} FinancialData
 * @property {string} accountLineId - Reference to AccountLine
 * @property {string} entityId - Reference to Entity
 * @property {number} balance - Current balance
 * @property {number} previousBalance - Previous period balance
 * @property {string} currency - Currency code
 * @property {Date} asOfDate - Date of the balance
 * @property {Object[]} transactions - Recent transactions affecting this account
 */

// Main accounting categories
export const ACCOUNT_CATEGORIES = {
  ASSETS: 'assets',
  LIABILITIES: 'liabilities', 
  EQUITY: 'equity',
  INCOME: 'income',
  EXPENSES: 'expenses'
};

// Category definitions with metadata
export const CATEGORY_DEFINITIONS = {
  [ACCOUNT_CATEGORIES.ASSETS]: {
    type: 'assets',
    name: 'Assets',
    description: 'Resources owned by the entity',
    icon: '🏦',
    color: '#10b981', // Green
    normalDebitBalance: true
  },
  [ACCOUNT_CATEGORIES.LIABILITIES]: {
    type: 'liabilities',
    name: 'Liabilities', 
    description: 'Debts and obligations owed to others',
    icon: '💳',
    color: '#ef4444', // Red
    normalDebitBalance: false
  },
  [ACCOUNT_CATEGORIES.EQUITY]: {
    type: 'equity',
    name: 'Equity',
    description: 'Owner\'s residual interest in the entity',
    icon: '👑',
    color: '#8b5cf6', // Purple
    normalDebitBalance: false
  },
  [ACCOUNT_CATEGORIES.INCOME]: {
    type: 'income',
    name: 'Income',
    description: 'Revenue and other income sources',
    icon: '💰',
    color: '#059669', // Dark green
    normalDebitBalance: false
  },
  [ACCOUNT_CATEGORIES.EXPENSES]: {
    type: 'expenses',
    name: 'Expenses',
    description: 'Costs incurred in business operations',
    icon: '💸',
    color: '#dc2626', // Dark red
    normalDebitBalance: true
  }
};

// Default chart of accounts structure
export const DEFAULT_ACCOUNT_LINES = [
  // ASSETS
  {
    id: 'cash-equivalents',
    name: 'Cash & Cash Equivalents',
    code: '1001',
    category: ACCOUNT_CATEGORIES.ASSETS,
    subcategory: 'Current Assets',
    description: 'Cash, checking accounts, savings accounts, and short-term investments',
    userConfigurable: true,
    dataSources: ['bank-checking', 'bank-savings', 'cash'],
    displayOrder: 1,
    isActive: true,
    metadata: { isLiquid: true, includeInQuickRatio: true }
  },
  {
    id: 'accounts-receivable',
    name: 'Accounts Receivable',
    code: '1100', 
    category: ACCOUNT_CATEGORIES.ASSETS,
    subcategory: 'Current Assets',
    description: 'Money owed by customers for goods or services delivered',
    userConfigurable: true,
    dataSources: ['invoicing-system', 'crm'],
    displayOrder: 2,
    isActive: true,
    metadata: { includeInCurrentRatio: true }
  },
  {
    id: 'inventory',
    name: 'Inventory',
    code: '1200',
    category: ACCOUNT_CATEGORIES.ASSETS,
    subcategory: 'Current Assets', 
    description: 'Raw materials, work-in-progress, and finished goods',
    userConfigurable: true,
    dataSources: ['inventory-system', 'warehouse-management'],
    displayOrder: 3,
    isActive: true,
    metadata: { includeInCurrentRatio: true }
  },
  {
    id: 'fixed-assets',
    name: 'Property, Plant & Equipment',
    code: '1500',
    category: ACCOUNT_CATEGORIES.ASSETS,
    subcategory: 'Fixed Assets',
    description: 'Long-term physical assets used in operations',
    userConfigurable: true,
    dataSources: ['asset-register', 'depreciation-schedule'],
    displayOrder: 4,
    isActive: true,
    metadata: { depreciable: true }
  },
  {
    id: 'investments',
    name: 'Investments',
    code: '1400',
    category: ACCOUNT_CATEGORIES.ASSETS,
    subcategory: 'Non-Current Assets',
    description: 'Long-term investments and securities',
    userConfigurable: true,
    dataSources: ['investment-portfolio', 'brokerage-accounts'],
    displayOrder: 5,
    isActive: true,
    metadata: { marketValued: true }
  },

  // LIABILITIES
  {
    id: 'accounts-payable',
    name: 'Accounts Payable',
    code: '2001',
    category: ACCOUNT_CATEGORIES.LIABILITIES,
    subcategory: 'Current Liabilities',
    description: 'Money owed to suppliers and vendors',
    userConfigurable: true,
    dataSources: ['expense-management', 'vendor-invoices'],
    displayOrder: 1,
    isActive: true,
    metadata: { includeInCurrentRatio: true }
  },
  {
    id: 'short-term-debt',
    name: 'Short-term Debt',
    code: '2100',
    category: ACCOUNT_CATEGORIES.LIABILITIES,
    subcategory: 'Current Liabilities',
    description: 'Loans and credit facilities due within one year',
    userConfigurable: true,
    dataSources: ['credit-cards', 'short-term-loans'],
    displayOrder: 2,
    isActive: true,
    metadata: { includeInCurrentRatio: true }
  },
  {
    id: 'long-term-debt',
    name: 'Long-term Debt',
    code: '2500',
    category: ACCOUNT_CATEGORIES.LIABILITIES,
    subcategory: 'Non-Current Liabilities',
    description: 'Loans and mortgages due after one year',
    userConfigurable: true,
    dataSources: ['mortgages', 'long-term-loans'],
    displayOrder: 3,
    isActive: true,
    metadata: { longTerm: true }
  },

  // EQUITY
  {
    id: 'owners-equity',
    name: 'Owner\'s Capital',
    code: '3001',
    category: ACCOUNT_CATEGORIES.EQUITY,
    subcategory: 'Owner\'s Equity',
    description: 'Initial and additional capital invested by owners',
    userConfigurable: true,
    dataSources: ['capital-contributions'],
    displayOrder: 1,
    isActive: true,
    metadata: { isCapital: true }
  },
  {
    id: 'retained-earnings',
    name: 'Retained Earnings',
    code: '3100',
    category: ACCOUNT_CATEGORIES.EQUITY,
    subcategory: 'Retained Earnings',
    description: 'Accumulated profits reinvested in the business',
    userConfigurable: false, // Calculated automatically
    dataSources: ['financial-statements'],
    displayOrder: 2,
    isActive: true,
    metadata: { isCalculated: true }
  },

  // INCOME
  {
    id: 'revenue',
    name: 'Revenue',
    code: '4001',
    category: ACCOUNT_CATEGORIES.INCOME,
    subcategory: 'Operating Revenue',
    description: 'Income from primary business activities',
    userConfigurable: true,
    dataSources: ['sales-system', 'invoicing', 'payment-processors'],
    displayOrder: 1,
    isActive: true,
    metadata: { isOperating: true }
  },
  {
    id: 'other-income',
    name: 'Other Income',
    code: '4500',
    category: ACCOUNT_CATEGORIES.INCOME,
    subcategory: 'Non-Operating Income',
    description: 'Income from investments, gains, and other sources',
    userConfigurable: true,
    dataSources: ['investment-returns', 'miscellaneous-income'],
    displayOrder: 2,
    isActive: true,
    metadata: { isOperating: false }
  },

  // EXPENSES
  {
    id: 'cost-of-sales',
    name: 'Cost of Goods Sold',
    code: '5001',
    category: ACCOUNT_CATEGORIES.EXPENSES,
    subcategory: 'Cost of Sales',
    description: 'Direct costs of producing goods or services',
    userConfigurable: true,
    dataSources: ['manufacturing-costs', 'direct-materials'],
    displayOrder: 1,
    isActive: true,
    metadata: { isDirectCost: true }
  },
  {
    id: 'operating-expenses',
    name: 'Operating Expenses',
    code: '6001',
    category: ACCOUNT_CATEGORIES.EXPENSES,
    subcategory: 'Operating Expenses',
    description: 'General business expenses and overhead',
    userConfigurable: true,
    dataSources: ['expense-reports', 'vendor-payments'],
    displayOrder: 2,
    isActive: true,
    metadata: { isOperating: true }
  },
  {
    id: 'personnel-costs',
    name: 'Personnel Costs',
    code: '6100',
    category: ACCOUNT_CATEGORIES.EXPENSES,
    subcategory: 'Operating Expenses',
    description: 'Salaries, wages, benefits, and contractor payments',
    userConfigurable: true,
    dataSources: ['payroll-system', 'contractor-payments'],
    displayOrder: 3,
    isActive: true,
    metadata: { isPersonnel: true }
  }
];

/**
 * Gets category definition by type
 * @param {string} categoryType - Category type
 * @returns {AccountCategory} Category definition
 */
export function getCategoryDefinition(categoryType) {
  return CATEGORY_DEFINITIONS[categoryType];
}

/**
 * Gets all account lines for a specific category
 * @param {string} categoryType - Category type to filter by
 * @returns {AccountLine[]} Account lines for the category
 */
export function getAccountLinesByCategory(categoryType) {
  return DEFAULT_ACCOUNT_LINES.filter(line => line.category === categoryType);
}

/**
 * Gets account line by ID
 * @param {string} accountLineId - Account line ID
 * @returns {AccountLine|null} Account line or null if not found
 */
export function getAccountLineById(accountLineId) {
  return DEFAULT_ACCOUNT_LINES.find(line => line.id === accountLineId) || null;
}

/**
 * Validates account line configuration
 * @param {Partial<AccountLine>} accountLine - Account line to validate
 * @returns {{isValid: boolean, errors: string[]}} Validation result
 */
export function validateAccountLine(accountLine) {
  const errors = [];

  if (!accountLine.name || accountLine.name.trim().length < 2) {
    errors.push('Account name must be at least 2 characters');
  }

  if (!accountLine.code || !/^\d{4}$/.test(accountLine.code)) {
    errors.push('Account code must be a 4-digit number');
  }

  if (!Object.values(ACCOUNT_CATEGORIES).includes(accountLine.category)) {
    errors.push('Invalid account category');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Calculates financial ratios
 * @param {FinancialData[]} financialData - Array of account balances
 * @returns {Object} Calculated financial ratios
 */
export function calculateFinancialRatios(financialData) {
  const balancesByCategory = {};
  
  // Group balances by category
  Object.values(ACCOUNT_CATEGORIES).forEach(category => {
    balancesByCategory[category] = financialData
      .filter(data => getAccountLineById(data.accountLineId)?.category === category)
      .reduce((sum, data) => sum + data.balance, 0);
  });

  const currentAssets = financialData
    .filter(data => {
      const line = getAccountLineById(data.accountLineId);
      return line?.category === ACCOUNT_CATEGORIES.ASSETS && 
             line?.subcategory === 'Current Assets';
    })
    .reduce((sum, data) => sum + data.balance, 0);

  const currentLiabilities = financialData
    .filter(data => {
      const line = getAccountLineById(data.accountLineId);
      return line?.category === ACCOUNT_CATEGORIES.LIABILITIES && 
             line?.subcategory === 'Current Liabilities';
    })
    .reduce((sum, data) => sum + data.balance, 0);

  return {
    totalAssets: balancesByCategory[ACCOUNT_CATEGORIES.ASSETS],
    totalLiabilities: balancesByCategory[ACCOUNT_CATEGORIES.LIABILITIES],
    totalEquity: balancesByCategory[ACCOUNT_CATEGORIES.EQUITY],
    totalIncome: balancesByCategory[ACCOUNT_CATEGORIES.INCOME],
    totalExpenses: balancesByCategory[ACCOUNT_CATEGORIES.EXPENSES],
    currentRatio: currentLiabilities > 0 ? currentAssets / currentLiabilities : 0,
    netIncome: balancesByCategory[ACCOUNT_CATEGORIES.INCOME] - balancesByCategory[ACCOUNT_CATEGORIES.EXPENSES],
    returnOnAssets: balancesByCategory[ACCOUNT_CATEGORIES.ASSETS] > 0 ? 
      (balancesByCategory[ACCOUNT_CATEGORIES.INCOME] - balancesByCategory[ACCOUNT_CATEGORIES.EXPENSES]) / balancesByCategory[ACCOUNT_CATEGORIES.ASSETS] : 0
  };
}

const AccountModel = {
  ACCOUNT_CATEGORIES,
  CATEGORY_DEFINITIONS,
  DEFAULT_ACCOUNT_LINES,
  getCategoryDefinition,
  getAccountLinesByCategory,
  getAccountLineById,
  validateAccountLine,
  calculateFinancialRatios
};

export default AccountModel; 